import { z } from "zod/v3";

/* ════════════════════════════════════════════════════════════════════════════
   RESEARCH ENGINE SCHEMAS — Dynamic Prompt Generation & Multi-Engine GEO
   ════════════════════════════════════════════════════════════════════════════ */

/* ── Brand Input ── */
export const BrandInputSchema = z.object({
  brandName: z.string().min(1),
  industry: z.string().min(1),
  website: z.string().url().optional(),
  competitors: z.array(z.string()).optional(),
  targetMarkets: z.array(z.string()).optional(),
  productDescription: z.string().optional(),
  additionalContext: z.string().optional(),
});
export type BrandInput = z.infer<typeof BrandInputSchema>;

/* ── Generated Query Plan (output of meta-prompt) ── */
export const GeneratedQueryPlanSchema = z.object({
  geo_seed_prompts: z
    .array(
      z.object({
        query: z.string(),
        category: z.enum(["buying_intent", "comparison", "recommendation", "informational"]),
        expected_context: z.string(),
      }),
    )
    .min(5)
    .max(25),

  xai_web_queries: z
    .array(
      z.object({
        query: z.string(),
        purpose: z.enum(["competitor_data", "industry_trends", "market_gaps", "brand_presence"]),
        allowed_domains: z.array(z.string()).optional(),
      }),
    )
    .min(2)
    .max(8),

  xai_x_queries: z
    .array(
      z.object({
        query: z.string(),
        purpose: z.enum([
          "brand_sentiment",
          "competitor_tracking",
          "trend_detection",
          "influencer_mapping",
        ]),
        allowed_handles: z.array(z.string()).optional(),
        date_range: z.enum(["7d", "30d", "90d"]),
        enable_media: z.boolean().optional(),
      }),
    )
    .min(3)
    .max(10),

  perplexity_queries: z
    .array(
      z.object({
        query: z.string(),
        purpose: z.enum(["deep_competitor_research", "market_sizing", "trend_analysis"]),
        recency_filter: z.enum(["day", "week", "month"]),
      }),
    )
    .min(2)
    .max(8),

  youtube_queries: z
    .array(
      z.object({
        query: z.string(),
        purpose: z.enum(["competitor_content", "trending_formats", "audience_engagement"]),
        max_results: z.number().min(3).max(25),
      }),
    )
    .min(1)
    .max(6),

  industry_context: z.object({
    key_terms: z.array(z.string()),
    buying_signals: z.array(z.string()),
    common_comparisons: z.array(z.string()),
    authority_domains: z.array(z.string()),
  }),
});
export type GeneratedQueryPlan = z.infer<typeof GeneratedQueryPlanSchema>;

/* ── Collection Result (per-query) ── */
export interface CollectionResult {
  source:
    | "perplexity_geo"
    | "gemini_geo"
    | "chatgpt_geo"
    | "perplexity_research"
    | "xai_web"
    | "xai_x"
    | "youtube";
  engine?: "perplexity" | "gemini" | "chatgpt";
  query: string;
  purpose: string;
  raw_data: string;
  brand_mentioned: boolean;
  competitors_mentioned: string[];
  citations: string[];
  grounding_sources?: string[];
  timestamp: string;
  latency_ms: number;
  error?: string;
}

/* ── GEO Visibility Matrix ── */
export interface GeoEngineScore {
  tested: number;
  brand_mentioned: number;
  mention_rate: number;
  avg_position: number | null;
  top_competitors: Array<{ name: string; mention_count: number }>;
}

export interface GeoPromptResult {
  query: string;
  category: string;
  perplexity: { mentioned: boolean; position: number | null };
  gemini: { mentioned: boolean; position: number | null };
  chatgpt: { mentioned: boolean; position: number | null };
}

export interface GeoVisibilityMatrix {
  prompts_tested: number;
  per_engine: Record<"perplexity" | "gemini" | "chatgpt", GeoEngineScore>;
  per_prompt: GeoPromptResult[];
}

/* ── Collection Report ── */
export interface CollectionReport {
  brand_input: BrandInput;
  query_plan: GeneratedQueryPlan;
  results: CollectionResult[];
  errors: Array<{ source: string; query: string; error: string }>;
  stats: { total: number; successful: number; failed: number; duration_ms: number };
  geo_matrix: GeoVisibilityMatrix;
}

/* ── Distillation Output (per section) ── */
export const SectionDistillationSchema = z.object({
  section: z.string(),
  findings: z
    .array(
      z.object({
        claim: z.string(),
        impact: z.string(),
        severity: z.enum(["high", "medium", "low"]),
        confidence: z.number().min(0).max(1),
        recommended_action: z.string(),
        evidence: z
          .array(
            z.object({
              source_url: z.string().nullable(),
              snippet: z.string(),
            }),
          )
          .max(6),
      }),
    )
    .max(12),
  risks: z.array(z.string()).max(10),
  opportunities: z.array(z.string()).max(10),
  kpis: z.array(z.string()).max(12),
});
export type SectionDistillation = z.infer<typeof SectionDistillationSchema>;

export interface DistilledIntelligence {
  sections: SectionDistillation[];
  geoMatrix: GeoVisibilityMatrix;
}

/* ── GEO Plan Schema ── */
export const GeoPlanSchema = z.object({
  overall_visibility_score: z.number().min(0).max(100),

  per_engine_scores: z.object({
    perplexity: z.object({
      score: z.number().min(0).max(100),
      mention_rate: z.number(),
      avg_position: z.number().nullable(),
      summary: z.string(),
    }),
    gemini: z.object({
      score: z.number().min(0).max(100),
      mention_rate: z.number(),
      avg_position: z.number().nullable(),
      summary: z.string(),
    }),
    chatgpt: z.object({
      score: z.number().min(0).max(100),
      mention_rate: z.number(),
      avg_position: z.number().nullable(),
      summary: z.string(),
    }),
  }),

  current_state: z.object({
    entity_clarity: z.enum(["clear", "partial", "missing"]),
    sentiment: z.enum(["positive", "neutral", "negative", "mixed"]),
    knowledge_gaps: z.array(z.string()),
    citation_sources: z.array(z.string()),
    competitor_advantages: z.array(
      z.object({
        competitor: z.string(),
        advantage: z.string(),
        queries_where_they_win: z.array(z.string()),
      }),
    ),
  }),

  plan: z.object({
    content_to_create: z.array(
      z.object({
        topic: z.string(),
        format: z.enum(["blog", "faq", "comparison", "guide", "case_study", "landing_page"]),
        priority: z.enum(["P1", "P2", "P3"]),
        rationale: z.string(),
        target_engines: z.array(z.string()),
      }),
    ),
    authority_targets: z.array(
      z.object({
        domain: z.string(),
        approach: z.string(),
      }),
    ),
    structured_data: z.array(z.string()),
    faq_topics: z.array(z.string()),
  }),

  priority_actions: z
    .array(
      z.object({
        action: z.string(),
        impact: z.string(),
        effort: z.enum(["S", "M", "L"]),
        timeline: z.string(),
      }),
    )
    .max(10),
});
export type GeoPlan = z.infer<typeof GeoPlanSchema>;

/* ── Ads Plan Schema ── */
export const AdsPlanSchema = z.object({
  google_ads: z.object({
    campaigns: z.array(
      z.object({
        name: z.string(),
        type: z.enum(["search", "pmax", "display", "video"]),
        monthly_budget: z.string(),
        keywords: z.array(z.string()),
        negative_keywords: z.array(z.string()),
        ad_copy: z.array(
          z.object({
            headline: z.string(),
            description: z.string(),
          }),
        ),
      }),
    ),
    audience_signals: z.array(z.string()),
    bidding_strategy: z.string(),
  }),
  xai_ads: z.object({
    campaigns: z.array(
      z.object({
        objective: z.string(),
        targeting: z.object({
          interests: z.array(z.string()),
          handle_targets: z.array(z.string()),
        }),
        creatives: z.array(
          z.object({
            format: z.string(),
            hook: z.string(),
            body: z.string(),
            cta: z.string(),
          }),
        ),
      }),
    ),
    budget_allocation: z.object({
      awareness_pct: z.number(),
      conversion_pct: z.number(),
    }),
  }),
});
export type AdsPlan = z.infer<typeof AdsPlanSchema>;

/* ── Content Strategy Plan Schema ── */
export const ContentPlanSchema = z.object({
  narrative: z.string(),
  content_pillars: z
    .array(
      z.object({
        pillar: z.string(),
        description: z.string(),
        formats: z.array(z.string()),
        frequency: z.string(),
        examples: z.array(z.string()),
      }),
    )
    .min(3)
    .max(5),
  four_week_calendar: z.array(
    z.object({
      week: z.number(),
      theme: z.string(),
      posts: z.array(
        z.object({
          platform: z.string(),
          format: z.string(),
          title: z.string(),
          hook: z.string(),
        }),
      ),
    }),
  ),
  competitor_content_gaps: z.array(z.string()),
  trending_formats_to_adopt: z.array(z.string()),
});
export type ContentPlan = z.infer<typeof ContentPlanSchema>;

/* ── Research Pipeline Steps ── */
export const RESEARCH_PIPELINE_STEPS = [
  { label: "Brand Input", icon: "settings", service: "User Input" },
  { label: "Data Collection", icon: "search", service: "Multi-Engine" },
  { label: "GEO Visibility", icon: "globe", service: "AI Search Audit" },
  { label: "Competitor Analysis", icon: "perplexity", service: "Deep Research" },
  { label: "Value Props", icon: "star", service: "Gemini Pro" },
  { label: "Content Strategy", icon: "file-text", service: "Gemini Pro" },
  { label: "Ads Plan", icon: "google-ads", service: "Gemini Pro" },
  { label: "Summary", icon: "zap", service: "System" },
];

/* ── Step 0: GEO Analysis ── */
export const geoSchema = z.object({
  summary: z.string().describe("2-3 sentence overview of the market"),
  demographics: z.string().describe("Key demographic insights for the region"),
  industries: z.string().describe("Dominant industries and economic drivers"),
  adLandscape: z.string().describe("Digital advertising landscape and trends"),
  opportunities: z.string().describe("Key marketing opportunities in this region"),
});

export function geoPrompt(geo: string, campaignName: string): string {
  return `You are a marketing research analyst. Analyze the following geographic market for a campaign called "${campaignName}".

Target Region: ${geo}

Provide a concise market analysis covering:
1. A 2-3 sentence market summary
2. Key demographics (age groups, income levels, digital adoption)
3. Dominant industries and economic drivers
4. Current digital advertising landscape and trends
5. Key marketing opportunities

Keep each section to 1-3 sentences. Be specific and actionable.`;
}

/* ── Step 1: Competitor Analysis ── */
export const competitorSchema = z.object({
  competitors: z
    .array(
      z.object({
        name: z.string().describe("Competitor company name"),
        spend: z.string().describe('Estimated monthly ad spend, e.g. "$45k/mo"'),
        angle: z.string().describe("Their current marketing angle/strategy"),
        weakness: z.string().describe("Key weakness or gap in their strategy"),
      }),
    )
    .min(3)
    .max(5),
});

export function competitorPrompt(geo: string, geoInsights: string, campaignName: string): string {
  return `You are a competitive intelligence analyst. Based on the market analysis below, identify the top 3 competitors in the "${campaignName}" space within ${geo}.

Market Context:
${geoInsights}

For each competitor provide:
- A realistic company name
- Estimated monthly ad spend (e.g. "$45k/mo")
- Their current marketing angle (1 sentence)
- A key weakness or gap we can exploit (1 sentence)

Return exactly 3 competitors.`;
}

/* ── Step 2: Value Propositions ── */
export const valuePropSchema = z.object({
  valueProps: z
    .array(
      z.object({
        id: z.string().describe("Unique slug identifier"),
        title: z.string().describe("Short title, 2-4 words"),
        description: z.string().describe("1-2 sentence description of this angle"),
        reasoning: z.string().describe("Why this angle works given the competitive landscape"),
        icon: z
          .enum(["gem", "clock", "target", "zap", "shield", "trending-up"])
          .describe("Icon name"),
      }),
    )
    .min(3)
    .max(3),
});

export function valuePropPrompt(geo: string, competitors: string, campaignName: string): string {
  return `You are a brand strategist. Based on the competitive landscape below, propose 3 unique value propositions for the "${campaignName}" campaign targeting ${geo}.

Competitor Landscape:
${competitors}

For each value proposition:
- Give it a short 2-4 word title
- Write a 1-2 sentence description
- Explain in 1 sentence why this angle works given the competition
- Choose an icon from: gem, clock, target, zap, shield, trending-up

Each proposition should exploit a different gap in the competition. Return exactly 3.`;
}

/* ── Step 3: Script Generation ── */
export const scriptSchema = z.object({
  script: z.string().describe("Full video script with [VISUAL] and [VOICEOVER] sections"),
  estimatedDuration: z.string().describe('Estimated video duration, e.g. "0:30"'),
});

export function scriptPrompt(
  selectedValueProp: string,
  geo: string,
  competitors: string,
  campaignName: string,
): string {
  return `You are a video scriptwriter for high-converting ad campaigns. Write a 30-second video script for "${campaignName}" targeting ${geo}.

Value Proposition: ${selectedValueProp}

Competitive context:
${competitors}

Format the script with alternating [VISUAL] and [VOICEOVER] sections:
- [VISUAL]: Describe what the viewer sees (camera angles, motion, text overlays)
- [VOICEOVER]: The spoken narration

Rules:
- 30 seconds total (about 75 words of voiceover)
- Open with a hook in the first 3 seconds
- Include a clear call-to-action at the end
- Use the value proposition as the central theme
- Make it punchy and modern`;
}

/* ── Step 4: Video Credit Estimate ── */
export const videoEstimateSchema = z.object({
  creditEstimate: z.number().describe("Estimated credits for video generation"),
  resolution: z.string().describe("Recommended resolution"),
  duration: z.string().describe("Video duration"),
  breakdown: z.string().describe("Brief breakdown of credit costs"),
});

/* ── Step 5: Automation Flow ── */
export const automationSchema = z.object({
  steps: z
    .array(
      z.object({
        type: z.enum(["delay", "email", "sms", "webhook"]),
        delay: z.string().optional().describe("Delay duration if type is delay"),
        subject: z.string().optional().describe("Email subject if type is email"),
        content: z.string().describe("Content or description of this step"),
      }),
    )
    .min(3)
    .max(6),
});

export function automationPrompt(
  campaignName: string,
  geo: string,
  selectedValueProp: string,
): string {
  return `You are a marketing automation specialist. Design a lead follow-up flow for the "${campaignName}" campaign targeting ${geo}.

Value Proposition: ${selectedValueProp}

Create a sequence of 3-5 automation steps triggered when a user clicks the ad and opts in.
Each step should be one of: delay, email, sms, or webhook.

For email steps, include a subject line and body preview.
For SMS steps, include the message text.
For delay steps, specify the wait time.

Make the sequence feel natural and not spammy.`;
}

/* ── Step 6: Launch Summary ── */
export const launchSummarySchema = z.object({
  summary: z.string().describe("Campaign summary paragraph"),
  totalCreditsUsed: z.number().describe("Total credits consumed"),
  assetsGenerated: z.array(z.string()).describe("List of assets created"),
  recommendations: z.string().describe("1-2 recommendations for optimization"),
});

export function launchSummaryPrompt(
  campaignData: Record<string, any>,
  mode: "legacy" | "research" = "legacy",
): string {
  if (mode === "research") {
    const geoScore = campaignData.geoPlan?.overall_visibility_score;
    const promptsTested =
      campaignData.geoMatrix?.prompts_tested ??
      campaignData.collectionReport?.stats?.total ??
      campaignData.collectionStats?.total ??
      0;
    const googleCampaigns = campaignData.adsPlan?.google_ads?.campaigns?.length ?? 0;
    const xCampaigns = campaignData.adsPlan?.xai_ads?.campaigns?.length ?? 0;
    const contentPillars = campaignData.contentPlan?.content_pillars?.length ?? 0;
    const creditsUsed = campaignData.totalCreditsUsed ?? 0;

    return `You are a campaign launch specialist. Summarize this RESEARCH-FIRST campaign using only the provided artifacts.

Campaign: ${campaignData.campaignName || "Untitled"}
Brand: ${campaignData.brandInput?.brandName || "Unknown"}
Industry: ${campaignData.brandInput?.industry || "Unknown"}
Research Prompts Tested: ${promptsTested}
GEO Visibility Score: ${typeof geoScore === "number" ? `${geoScore}/100` : "Unknown"}
Content Pillars: ${contentPillars}
Ads Campaigns Planned: Google=${googleCampaigns}, X/Twitter=${xCampaigns}
Authoritative Credits Used: ${creditsUsed}

Rules:
- Base the summary strictly on geoPlan, contentPlan, adsPlan, and collection stats.
- Do NOT use legacy flow assumptions (geo/script/video/automation) unless explicitly present.
- Use the provided Authoritative Credits Used value exactly for totalCreditsUsed.

Provide:
1. A concise summary paragraph (2-3 sentences)
2. totalCreditsUsed (must equal Authoritative Credits Used)
3. assetsGenerated as concrete artifacts from the research pipeline
4. 1-2 optimization recommendations`;
  }

  return `You are a campaign launch specialist. Summarize the following campaign that is ready to launch:

Campaign: ${campaignData.campaignName || "Untitled"}
Region: ${campaignData.geo || "Global"}
Value Proposition: ${campaignData.selectedValuePropTitle || campaignData.selectedValueProp || "Not specified"}
Script: ${campaignData.script ? "Approved" : "Pending"}
Video: ${campaignData.videoAssetUrl ? "Generated" : "Pending"}
Automation: ${campaignData.automationSteps ? `${campaignData.automationSteps.length} steps configured` : "Not configured"}

Provide:
1. A concise summary paragraph (2-3 sentences)
2. Total credits used (estimate based on: 1 credit per 1K tokens LLM, 150 per min video)
3. List of assets generated
4. 1-2 optimization recommendations`;
}
